import type { EnrichedFeature } from '@/hooks/useGeoLayer';
import type { MondayItem } from '@/hooks/useMondayBoardItems';
import { getDrawerHeaderMeta, getDrawerRegistryFields } from './drawer-attributes';
import { StarRating } from './StarRating';
import styles from './SiteDrawer.module.css';

interface Props {
  feature: EnrichedFeature;
  mondayItem: MondayItem | null;
  onClose: () => void;
  variant?: 'default' | 'agritour';
}

const SELECTION_MODE_LABELS: Record<string, string> = {
  point: 'נקודה על המפה',
  polygon_drawn: 'פוליגון מצויר',
};

const BADGE_LABELS: Record<string, string> = { active: 'פעיל', lead: 'ליד', inactive: 'לא פעיל' };
const BADGE_CLASS: Record<string, string> = {
  active: styles.badgeActive,
  lead: styles.badgeLead,
  inactive: styles.badgeInactive,
};

function attrString(attrs: Record<string, unknown>, key: string): string | null {
  const val = attrs[key];
  if (val === null || val === undefined) return null;
  const text = String(val).trim();
  return text.length > 0 ? text : null;
}

export function SiteDrawer({ feature, mondayItem, onClose, variant = 'default' }: Props) {
  const { crm, name } = feature;
  const isEntrepreneurs = feature.layerId === 'entrepreneurs';
  const isAgritourLayer = feature.layerId === 'agritour-entrepreneurs';
  const headerMeta = getDrawerHeaderMeta(feature);
  const registryFields = isEntrepreneurs || isAgritourLayer ? [] : getDrawerRegistryFields(feature);
  const mondayItemUrl = attrString(feature.attributes, 'monday_item_url');
  const selectionMode = attrString(feature.attributes, 'selection_mode');
  const drawerClassName =
    variant === 'agritour' ? `${styles.drawer} ${styles.drawerAgritour}` : styles.drawer;

  return (
    <div className={drawerClassName} data-testid="site-drawer">
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.titleBlock}>
            <h2 className={styles.name}>{name}</h2>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="סגור"
          >
            ×
          </button>
        </div>
        <div className={styles.headerMeta}>
          {headerMeta && <p className={styles.metaLine}>{headerMeta}</p>}
          <span className={`${styles.badge} ${BADGE_CLASS[crm.status] ?? ''}`}>
            {BADGE_LABELS[crm.status]}
          </span>
        </div>
      </div>

      <div className={styles.body}>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>פרטי קשר</h3>
          <dl className={styles.dl}>
            <dt className={styles.dt}>{isAgritourLayer ? 'שם מגיש' : 'מנהל'}</dt>
            <dd className={styles.dd}>{crm.manager}</dd>
            <dt className={styles.dt}>טלפון</dt>
            <dd className={styles.dd}><a href={`tel:${crm.phone}`}>{crm.phone}</a></dd>
            <dt className={styles.dt}>אימייל</dt>
            <dd className={styles.dd}><a href={`mailto:${crm.email}`}>{crm.email}</a></dd>
          </dl>
        </section>

        {isAgritourLayer ? (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>פרטי בקשה</h3>
            <dl className={styles.dl}>
              {attrString(feature.attributes, 'venture_name') && (
                <>
                  <dt className={styles.dt}>שם מיזם</dt>
                  <dd className={styles.dd}>{attrString(feature.attributes, 'venture_name')}</dd>
                </>
              )}
              {attrString(feature.attributes, 'settlement') && (
                <>
                  <dt className={styles.dt}>יישוב</dt>
                  <dd className={styles.dd}>{attrString(feature.attributes, 'settlement')}</dd>
                </>
              )}
              {selectionMode && (
                <>
                  <dt className={styles.dt}>סוג בחירה</dt>
                  <dd className={styles.dd}>
                    {SELECTION_MODE_LABELS[selectionMode] ?? selectionMode}
                  </dd>
                </>
              )}
              {mondayItemUrl && (
                <>
                  <dt className={styles.dt}>Monday</dt>
                  <dd className={styles.dd}>
                    <a href={mondayItemUrl} target="_blank" rel="noopener noreferrer">
                      פתח בקשה בלוח
                    </a>
                  </dd>
                </>
              )}
            </dl>
          </section>
        ) : isEntrepreneurs ? (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>תיק יזם</h3>
            <dl className={styles.dl}>
              <dt className={styles.dt}>מגרש</dt>
              <dd className={styles.dd}>{attrString(feature.attributes, 'migrash') ?? '—'}</dd>
              {attrString(feature.attributes, 'designation') && (
                <>
                  <dt className={styles.dt}>ייעוד</dt>
                  <dd className={styles.dd}>{attrString(feature.attributes, 'designation')}</dd>
                </>
              )}
              {attrString(feature.attributes, 'venture_type') && (
                <>
                  <dt className={styles.dt}>סוג מיזם</dt>
                  <dd className={styles.dd}>{attrString(feature.attributes, 'venture_type')}</dd>
                </>
              )}
              {attrString(feature.attributes, 'assessment_status') && (
                <>
                  <dt className={styles.dt}>סטטוס הערכה</dt>
                  <dd className={styles.dd}>{attrString(feature.attributes, 'assessment_status')}</dd>
                </>
              )}
              {crm.notes && (
                <>
                  <dt className={styles.dt}>סיכום</dt>
                  <dd className={styles.dd}>{crm.notes}</dd>
                </>
              )}
              {mondayItemUrl && (
                <>
                  <dt className={styles.dt}>Monday</dt>
                  <dd className={styles.dd}>
                    <a href={mondayItemUrl} target="_blank" rel="noopener noreferrer">
                      פתח תיק בלוח
                    </a>
                  </dd>
                </>
              )}
            </dl>
          </section>
        ) : (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>נתוני מלון</h3>
            <dl className={styles.dl}>
              <dt className={styles.dt}>חדרים</dt>
              <dd className={styles.dd}>{crm.rooms}</dd>
              <dt className={styles.dt}>דירוג</dt>
              <dd className={styles.dd}>
                <StarRating stars={crm.stars} />
                <span className={styles.starCaption}>{crm.stars} כוכבים</span>
              </dd>
              {crm.notes && (
                <>
                  <dt className={styles.dt}>הערות</dt>
                  <dd className={styles.dd}>{crm.notes}</dd>
                </>
              )}
            </dl>
          </section>
        )}

        {registryFields.length > 0 && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>רישום רשמי</h3>
            <dl className={styles.dl}>
              {registryFields.map((field) => (
                <div key={field.key} style={{ display: 'contents' }}>
                  <dt className={styles.dt}>{field.label}</dt>
                  <dd className={styles.dd}>
                    {field.href ? (
                      <a href={field.href} target="_blank" rel="noopener noreferrer">
                        {field.value}
                      </a>
                    ) : (
                      field.value
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {mondayItem && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>פריט Monday</h3>
            <dl className={styles.dl}>
              {mondayItem.columnValues
                .filter((cv) => cv.text)
                .map((cv) => (
                  <div key={cv.id} style={{ display: 'contents' }}>
                    <dt className={styles.dt}>{cv.title}</dt>
                    <dd className={styles.dd}>{cv.text}</dd>
                  </div>
                ))}
            </dl>
          </section>
        )}
      </div>
    </div>
  );
}
